/**
 * Simulador de activación en planta (#41).
 *
 * **Es el acto donde se explica por qué una etiqueta robada no autentica.**
 * Hasta que una unidad pasa por acá `verify` responde "no pudimos verificar
 * este código" y el motor de anomalías levanta una alerta crítica por cada
 * escaneo. Las etiquetas llegan de fábrica ya registradas; si nacieran
 * válidas, un rollo robado del depósito autenticaría producto falsificado.
 *
 * Por eso esta pantalla está diseñada alrededor de lo que sale MAL —estado
 * inválido, lote ajeno, etiqueta ya activada, código ilegible— y no alrededor
 * del caso feliz, que en la línea es el que nadie mira.
 *
 * Se activa con el CÓDIGO IMPRESO, nunca con el `unit_id`: aceptar ids
 * internos permitiría activar etiquetas sin tenerlas físicamente delante, que
 * es justo lo que hace un rollo robado con alguien de adentro colaborando.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useKeysetQuery } from '@/api/hooks';
import { activationKeys } from '@/api/keys';
import type { components } from '@/api/schema';
import type { ApiError } from '@/api/errors';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import type { MessageKey } from '@/i18n/messages';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { RunResultCard } from './ActivationResult';
import { parseScan, shortCode, suggestBatchKey } from './codes';
import { dequeue, enqueue, queuedCodes, toBulkBody, type PendingRun } from './queue';

type Batch = components['schemas']['BatchOut'];
type ActivationBatch = components['schemas']['ActivationBatchOut'];
type ActivationResult = components['schemas']['ActivationResultOut'];

/** Lotes que ofrece el selector. Una marca no tiene miles abiertos a la vez. */
const BATCH_OPTIONS = 100;
const RUNS_PAGE = 25;
/** Cuántos códigos activados se muestran para ir a verificarlos. */
const VERIFY_SAMPLE = 5;

interface CompletedRun {
  run: PendingRun;
  result: ActivationResult;
  at: number;
}

export function ActivationPage(): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();

  const batches = useKeysetQuery<Batch>(
    activationKeys.batchOptions(BATCH_OPTIONS),
    '/api/v1/batches',
    undefined,
    { limit: BATCH_OPTIONS },
  );
  const runs = useKeysetQuery<ActivationBatch>(
    activationKeys.list(RUNS_PAGE),
    '/api/v1/activations',
    undefined,
    { limit: RUNS_PAGE },
  );

  const [batchId, setBatchId] = useState<number | null>(null);
  const [lineId, setLineId] = useState('LINEA-3');
  const [sequence, setSequence] = useState(1);
  const [customKey, setCustomKey] = useState<string | null>(null);
  const [raw, setRaw] = useState('');
  const [offline, setOffline] = useState(false);
  const [queue, setQueue] = useState<PendingRun[]>([]);
  const [completed, setCompleted] = useState<CompletedRun[]>([]);
  const [notice, setNotice] = useState<MessageKey | null>(null);
  const [syncing, setSyncing] = useState(false);

  const scan = useMemo(() => parseScan(raw), [raw]);
  const suggestedKey = suggestBatchKey(lineId, sequence, new Date());
  const batchKey = customKey ?? suggestedKey;
  const trimmedLine = lineId.trim();

  const send = useMutation<ActivationResult, ApiError, PendingRun>({
    mutationFn: (run) => api.post<ActivationResult>('/api/v1/activations/bulk', toBulkBody(run)),
    onSuccess: (result, run) => {
      setCompleted((previous) => [{ run, result, at: Date.now() }, ...previous].slice(0, 10));
      setQueue((previous) => dequeue(previous, run.clientBatchId));
      // La activación cambia `batch.activated_count` y agrega un lote a la
      // lista de recibidos: las dos listas de esta pantalla quedan viejas.
      void queryClient.invalidateQueries({ queryKey: activationKeys.all });
    },
  });

  const ready = batchId !== null && scan.codes.length > 0 && batchKey.trim().length > 0;
  // Qué falta para poder activar, en orden de lo que se completa primero. Un
  // botón deshabilitado con "0 listos" al lado no dice por qué (#172): el
  // operario tiene que saber si es el lote, los códigos o la clave.
  const falta: MessageKey | null =
    batchId === null
      ? 'activation.missing.batch'
      : scan.codes.length === 0
        ? 'activation.missing.codes'
        : batchKey.trim().length === 0
          ? 'activation.missing.key'
          : null;

  function buildRun(): PendingRun | null {
    if (batchId === null || scan.codes.length === 0) return null;
    return {
      clientBatchId: batchKey.trim(),
      batchId,
      lineId: trimmedLine.length > 0 ? trimmedLine : null,
      codes: scan.codes,
      // El reloj de la LÍNEA. Se conserva al reenviar desde la cola: es el
      // dato de cuándo se envasó de verdad, no de cuándo volvió la red.
      clientSentAt: new Date().toISOString(),
    };
  }

  function nextTurn(): void {
    setRaw('');
    setCustomKey(null);
    setSequence((value) => value + 1);
  }

  function submit(): void {
    const run = buildRun();
    if (run === null) return;
    setNotice(null);

    if (offline) {
      const result = enqueue(queue, run);
      setQueue(result.queue);
      setNotice(result.replaced ? 'activation.offline.replaced' : 'activation.offline.stored');
      nextTurn();
      return;
    }

    // El textarea se limpia sólo si el servidor aceptó: con un 409 por clave
    // repetida, perder las lecturas obligaría a escanear la caja de nuevo.
    send.mutate(run, { onSuccess: () => { nextTurn(); } });
  }

  async function sync(): Promise<void> {
    setSyncing(true);
    setNotice(null);
    try {
      // Secuencial y en orden de lectura: los conflictos se resuelven por
      // orden de llegada al servidor, así que el orden importa.
      for (const run of queue) {
        await send.mutateAsync(run);
      }
    } catch {
      // El error queda en `send.error` y la tanda sigue en la cola.
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader
          title={t('activation.title')}
          description={t('activation.description')}
          actions={
            <Link
              to="/activation/modes"
              className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-accent underline underline-offset-2"
            >
              {t('activation.modes.link')}
            </Link>
          }
        />
        <CardBody>
          <p className="max-w-prose text-sm text-ink-muted">{t('activation.rule')}</p>
        </CardBody>
      </Card>

      {/* ─────────────────────────────────────────────── consola de línea */}
      <Card>
        <CardHeader
          title={t('activation.console.title')}
          description={t('activation.console.description')}
          actions={
            <Badge tone={offline ? 'suspect' : 'verified'}>
              {offline ? t('activation.offline.on') : t('activation.offline.off')}
            </Badge>
          }
        />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('activation.field.batch')}
              </span>
              <select
                className="mt-1 h-10 w-full rounded border border-border-strong bg-surface px-2 text-sm text-ink"
                value={batchId === null ? '' : String(batchId)}
                disabled={batches.query.isPending}
                onChange={(event) => {
                  const value = event.target.value;
                  setBatchId(value === '' ? null : Number.parseInt(value, 10));
                }}
              >
                <option value="">{t('activation.field.batchPlaceholder')}</option>
                {batches.items.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.lot_code}
                    {batch.product_sku === null || batch.product_sku === undefined
                      ? ''
                      : ` · ${batch.product_sku}`}
                    {batch.status === 'recalled' || batch.status === 'closed'
                      ? ` · ${t('activation.field.batchBlocked')}`
                      : ''}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-ink-subtle">
                {t('activation.field.batchHint')}
              </span>
            </label>

            <label className="block">
              <span className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('activation.field.line')}
              </span>
              <input
                className="mt-1 h-10 w-full rounded border border-border-strong bg-surface px-2 text-sm text-ink"
                value={lineId}
                onChange={(event) => { setLineId(event.target.value); }}
              />
              <span className="mt-1 block text-xs text-ink-subtle">
                {t('activation.field.lineHint')}
              </span>
            </label>
          </div>

          <label className="block">
            <span className="text-xs tracking-wide text-ink-subtle uppercase">
              {t('activation.field.key')}
            </span>
            <input
              className="mt-1 h-10 w-full rounded border border-border-strong bg-surface px-2 font-mono text-sm text-ink"
              value={batchKey}
              onChange={(event) => { setCustomKey(event.target.value); }}
            />
            <span className="mt-1 block text-xs text-ink-subtle">
              {t('activation.field.keyHint')}
            </span>
          </label>

          <label className="block">
            <span className="text-xs tracking-wide text-ink-subtle uppercase">
              {t('activation.field.codes')}
            </span>
            <textarea
              className="mt-1 h-40 w-full rounded border border-border-strong bg-surface px-2 py-2 font-mono text-sm text-ink"
              value={raw}
              spellCheck={false}
              placeholder={t('activation.field.codesPlaceholder')}
              onChange={(event) => { setRaw(event.target.value); }}
            />
            <span className="mt-1 block text-xs text-ink-subtle">
              {t('activation.field.codesHint')}
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={scan.codes.length > 0 ? 'verified' : 'neutral'}>
              {t('activation.scan.ready', { count: fmt.number(scan.codes.length) })}
            </Badge>
            {scan.duplicates.length > 0 && (
              <Badge tone="suspect">
                {t('activation.scan.duplicates', { count: fmt.number(scan.duplicates.length) })}
              </Badge>
            )}
            {scan.unreadable.length > 0 && (
              <Badge tone="critical">
                {t('activation.scan.unreadable', { count: fmt.number(scan.unreadable.length) })}
              </Badge>
            )}
          </div>

          {scan.unreadable.length > 0 && (
            <p className="text-xs text-ink-muted">
              {t('activation.scan.unreadableHint')}{' '}
              <span className="font-mono">
                {scan.unreadable.slice(0, 3).map(shortCode).join(' · ')}
              </span>
            </p>
          )}

          {notice !== null && (
            <p className="rounded border border-border bg-surface-sunken p-3 text-sm text-ink-muted">
              {t(notice)}
            </p>
          )}

          {send.isError && <ErrorState error={send.error} />}
        </CardBody>

        <CardBody className="flex flex-wrap items-center gap-3 border-t border-border">
          <Button variant="primary" disabled={!ready || send.isPending} onClick={submit}>
            {send.isPending
              ? t('activation.submitting')
              : offline
                ? t('activation.submit.offline')
                : t('activation.submit')}
          </Button>
          {falta !== null && !send.isPending && (
            <span className="text-sm text-ink-muted" role="status">
              {t(falta)}
            </span>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              setOffline((value) => !value);
              setNotice(null);
            }}
          >
            {offline ? t('activation.offline.reconnect') : t('activation.offline.disconnect')}
          </Button>
          <p className="text-xs text-ink-subtle">{t('activation.offline.explain')}</p>
        </CardBody>
      </Card>

      {/* ───────────────────────────────────────────────── cola sin red */}
      {queue.length > 0 && (
        <Card>
          <CardHeader
            title={t('activation.offline.queueTitle')}
            description={t('activation.offline.queueDescription', {
              runs: fmt.number(queue.length),
              codes: fmt.number(queuedCodes(queue)),
            })}
            actions={
              <Button
                variant="primary"
                size="sm"
                disabled={syncing || offline}
                onClick={() => { void sync(); }}
              >
                {syncing ? t('activation.offline.syncing') : t('activation.offline.sync')}
              </Button>
            }
          />
          <Table caption={t('activation.offline.queueTitle')}>
            <thead>
              <tr>
                <Th>{t('activation.field.key')}</Th>
                <Th>{t('activation.field.batch')}</Th>
                <Th>{t('activation.runs.column.requested')}</Th>
                <Th>{t('activation.offline.queuedAt')}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {queue.map((run) => (
                <tr key={run.clientBatchId}>
                  <Td className="font-mono">{run.clientBatchId}</Td>
                  <Td className="text-ink-muted">#{run.batchId}</Td>
                  <Td className="font-mono">{fmt.number(run.codes.length)}</Td>
                  <Td className="text-ink-muted">{fmt.dateTime(run.clientSentAt)}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setQueue((previous) => dequeue(previous, run.clientBatchId)); }}
                    >
                      {t('activation.offline.discard')}
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* ──────────────────────────────────── resultados de esta sesión */}
      {completed.map((entry) => (
        <RunResultCard
          key={`${entry.run.clientBatchId}-${entry.at}`}
          result={entry.result}
          activatedSample={activatedSample(entry)}
          actions={
            <>
              <Button
                size="sm"
                variant="secondary"
                disabled={send.isPending || offline}
                onClick={() => { send.mutate(entry.run); }}
              >
                {t('activation.result.resend')}
              </Button>
              <Link
                to={`/activation/${entry.result.activation_batch_id}`}
                className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-accent underline underline-offset-2"
              >
                {t('activation.result.open')}
              </Link>
            </>
          }
        />
      ))}

      {/* ─────────────────────────────────── lotes de activación recibidos */}
      <Card>
        <CardHeader
          title={t('activation.runs.title')}
          description={t('activation.runs.description')}
        />

        {runs.query.isPending && <LoadingState rows={4} />}

        {runs.query.isError && (
          <CardBody>
            <ErrorState
              error={runs.query.error}
              onRetry={() => { void runs.query.refetch(); }}
            />
          </CardBody>
        )}

        {!runs.query.isPending && !runs.query.isError && runs.items.length === 0 && (
          <EmptyState title={t('activation.runs.empty')} detail={t('activation.runs.emptyHint')} />
        )}

        {runs.items.length > 0 && (
          <>
            <Table caption={t('activation.runs.description')}>
              <thead>
                <tr>
                  <Th>{t('activation.field.key')}</Th>
                  <Th>{t('activation.field.line')}</Th>
                  <Th>{t('activation.runs.column.received')}</Th>
                  <Th>{t('activation.runs.column.requested')}</Th>
                  <Th>{t('activation.result.activated')}</Th>
                  <Th>{t('activation.result.rejected')}</Th>
                </tr>
              </thead>
              <tbody>
                {runs.items.map((run) => (
                  <tr key={run.id}>
                    <Td>
                      <Link
                        to={`/activation/${run.id}`}
                        className="font-mono text-accent underline underline-offset-2"
                      >
                        {run.client_batch_id}
                      </Link>
                    </Td>
                    <Td className="text-ink-muted">{run.line_id ?? '—'}</Td>
                    <Td className="text-ink-muted">{fmt.dateTime(run.received_at)}</Td>
                    <Td className="font-mono">{fmt.number(run.requested)}</Td>
                    <Td className="font-mono text-verified">{fmt.number(run.activated)}</Td>
                    <Td className="font-mono">
                      {run.rejected > 0 ? (
                        <span className="text-critical">{fmt.number(run.rejected)}</span>
                      ) : (
                        fmt.number(0)
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {runs.hasMore ? (
                <Button size="sm" onClick={runs.loadMore} disabled={runs.isLoadingMore}>
                  {runs.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Códigos que quedaron activados, para ir a verificarlos como consumidor.
 *
 * Sólo se calcula cuando la muestra de rechazos está completa: con la muestra
 * truncada (más de 100 rechazos) restar los rechazados daría códigos que en
 * realidad no se activaron, y mandar a alguien a verificar una etiqueta que
 * no autentica es peor que no ofrecer la lista.
 */
function activatedSample(entry: CompletedRun): string[] {
  const rejects = entry.result.rejects ?? [];
  if (entry.result.activated === 0) return [];
  if (entry.result.rejected > rejects.length) return [];
  const rejected = new Set(rejects.map((reject) => reject.code));
  return entry.run.codes.filter((code) => !rejected.has(code)).slice(0, VERIFY_SAMPLE);
}
